import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, UserPlus } from "lucide-react";
import api, { fmtDate, money } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, Stat, Th, Td, AdminHeading } from "@/components/admin/ui";
import StatusBadge from "@/components/StatusBadge";
import WeatherPanel from "@/components/WeatherPanel";
import { ScheduleAutomationHealth } from "@/components/admin/ScheduleAutomationHealth";
import { EmailBrandingChip } from "@/components/admin/EmailBrandingChip";

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [leads, setLeads] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => setD(false));
    api.get("/admin/leads/summary").then((r) => setLeads(r.data.cards)).catch(() => setLeads(null));
  }, []);
  if (d === null) return <Loading />;
  if (!d) return <p className="text-muted-foreground">Unable to load dashboard.</p>;
  const c = d.cards;

  const Table = ({ rows, empty }) => rows.length === 0 ? <p className="text-sm text-muted-foreground py-4">{empty}</p> : (
    <table className="w-full">
      <thead><tr><Th>Conf.</Th><Th>Guest</Th><Th>Experience</Th><Th>Status</Th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.id} className="cursor-pointer hover:bg-white/5" onClick={() => nav(`/admin/reservations/${r.id}`)}>
          <Td className="text-gold">{r.confirmation_number}</Td><Td>{r.guest}</Td><Td>{r.experience}</Td><Td><StatusBadge status={r.status} /></Td>
        </tr>
      ))}</tbody>
    </table>
  );

  return (
    <div>
      <AdminHeading title="Today at The D" subtitle={new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <EmailBrandingChip />
          <button onClick={() => nav("/admin/reservations?new=1")} className="btn-gold px-5 py-2.5 text-sm" data-testid="quick-new-reservation">New Reservation</button>
        </div>
      </AdminHeading>

      {!d.smtp_configured && (
        <div className="flex items-center gap-3 bg-gold/10 border border-gold/30 rounded-lg px-4 py-3 mb-6 text-sm">
          <AlertTriangle size={16} className="text-gold" /> Email sending is <b>not configured</b>. Templates & history work; add SMTP credentials in Configuration to send.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Arrivals Today" value={c.arrivals_today} accent="pool" testid="stat-arrivals" onClick={() => nav("/admin/reservations?filter=arrivals-today")} />
        <Stat label="Departures Today" value={c.departures_today} accent="pool" testid="stat-departures" onClick={() => nav("/admin/reservations?filter=departures-today")} />
        <Stat label="In House" value={c.in_house} accent="gold" testid="stat-inhouse" onClick={() => nav("/admin/in-house")} />
        <Stat label="Pending Requests" value={c.pending_requests} accent="gold" testid="stat-pending-req" onClick={() => nav("/admin/reservations?status=Request Submitted")} />
        <Stat label="Incomplete Check-ins" value={c.incomplete_checkins} accent="gold" testid="stat-incomplete" onClick={() => nav("/admin/reservations?filter=incomplete-checkin")} />
        <Stat label="Unpaid Balances" value={c.unpaid_count} accent="red" testid="stat-unpaid" onClick={() => nav("/admin/reservations?filter=unpaid")} />
        <Stat label="Open Concierge" value={c.open_concierge} accent="gold" testid="stat-concierge" onClick={() => nav("/admin/concierge?status=Open")} />
        <Stat label="Reservations / Month" value={c.reservations_this_month} accent="pool" testid="stat-res-month" onClick={() => nav("/admin/reservations?filter=this-month")} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Pending Access Requests" value={c.pending_access_requests || 0} accent="gold" onClick={() => nav("/admin/access-requests")} data-testid="stat-access" />
        <Stat label="Pending Referrals" value={c.pending_referrals || 0} accent="gold" onClick={() => nav("/admin/referrals")} data-testid="stat-referrals" />
        <Stat label="D Drafts Awaiting Review" value={c.d_drafts_awaiting || 0} accent="pool" onClick={() => nav("/admin/communications")} data-testid="stat-ddrafts" />
        <Stat label="Pending Activation Reminders" value={c.pending_reminders || 0} accent="pool" onClick={() => nav("/admin/access-reminders")} data-testid="stat-reminders" />
      </div>

      {leads && (
        <div className="mb-8">
          <p className="text-[11px] tracking-label uppercase text-muted-foreground mb-3">Public D Inquiries &amp; Leads</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="New Public D Inquiries" value={leads.new_public_d || 0} accent="gold" testid="lead-new" onClick={() => nav("/admin/ghost-settings?tab=public")} />
            <Stat label="Follow-Up Required" value={leads.follow_up_required || 0} accent="red" testid="lead-followup" onClick={() => nav("/admin/ghost-settings?tab=public&followup=1")} />
            <Stat label="Invitation Requests" value={leads.invitation_requests || 0} accent="pool" testid="lead-invitation" onClick={() => nav("/admin/ghost-settings?tab=public&type=invitation")} />
            <Stat label="Event Inquiries" value={leads.event_inquiries || 0} accent="pool" testid="lead-event" onClick={() => nav("/admin/ghost-settings?tab=public&type=event")} />
            <Stat label="Reservation Interest" value={leads.reservation_interest || 0} accent="pool" testid="lead-reservation" onClick={() => nav("/admin/ghost-settings?tab=public&type=reservation")} />
            <Stat label="Membership Interest" value={leads.membership_interest || 0} accent="gold" testid="lead-membership" onClick={() => nav("/admin/ghost-settings?tab=public&type=membership")} />
            <Stat label="Access Requests" value={leads.access_requests || 0} accent="gold" testid="lead-access" onClick={() => nav("/admin/access-requests")} />
          </div>
        </div>
      )}

      <div className="mb-8">
        <ScheduleAutomationHealth />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <WeatherPanel variant="card" admin className="lg:col-span-2" />
        {d.referrals && Object.keys(d.referrals).length > 0 && (
          <Panel title="Referral Pipeline">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
              <div><span className="text-muted-foreground">Submitted this month:</span> {d.referrals.submitted_this_month}</div>
              <div><span className="text-muted-foreground">Pending host review:</span> {d.referrals.pending_host_review}</div>
              <div><span className="text-muted-foreground">Invitations awaiting:</span> {d.referrals.invitations_awaiting}</div>
              <div><span className="text-muted-foreground">Pending activation:</span> {d.referrals.pending_activation}</div>
              <div><span className="text-muted-foreground">Reservation conversions:</span> {d.referrals.reservation_conversions}</div>
              <div><span className="text-muted-foreground">First stays:</span> {d.referrals.first_stays}</div>
              {d.referrals.link_review > 0 && <div className="text-amber-300"><span className="text-muted-foreground">Link review:</span> {d.referrals.link_review}</div>}
              {d.referrals.recognition_awaiting > 0 && <div className="text-gold"><span className="text-muted-foreground">Recognition awaiting:</span> {d.referrals.recognition_awaiting}</div>}
            </div>
            <button onClick={() => nav("/admin/referrals")} className="btn-ghost-gold px-4 py-2 text-xs mt-4" data-testid="dash-open-referrals">Open Referral Tracking</button>
          </Panel>
        )}
        {d.access_requests && (
          <Panel title="Pending Access Requests">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-serif text-4xl text-gold">{d.access_requests.pending}</p>
                <p className="text-xs text-muted-foreground">awaiting review</p>
              </div>
              <button onClick={() => nav("/admin/access-requests")} className="btn-ghost-gold px-4 py-2 text-xs inline-flex items-center gap-2" data-testid="dash-review-access"><UserPlus size={14} /> Review</button>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <div><span className="text-muted-foreground">New today:</span> {d.access_requests.new_today}</div>
              <div><span className="text-muted-foreground">Invitation-linked:</span> {d.access_requests.invite_linked}</div>
              <div><span className="text-muted-foreground">More info needed:</span> {d.access_requests.more_info}</div>
              {d.access_requests.restricted > 0 && <div className="text-rose-300"><span className="text-muted-foreground">Restricted match:</span> {d.access_requests.restricted}</div>}
              {d.access_requests.oldest_created_at && <div className="col-span-2"><span className="text-muted-foreground">Oldest pending:</span> {fmtDate(d.access_requests.oldest_created_at)}</div>}
            </div>
          </Panel>
        )}
        <Panel title="Pending Requests"><Table rows={d.pending_requests} empty="No pending requests." /></Panel>
        <Panel title="Arrivals Today"><Table rows={d.arrivals_today} empty="No arrivals today." /></Panel>
        <Panel title="In House Now"><Table rows={d.in_house} empty="No guests in house." /></Panel>
        <Panel title="Unpaid Balances">
          {d.unpaid_balances.length === 0 ? <p className="text-sm text-muted-foreground py-4">All balances settled.</p> : (
            <table className="w-full"><thead><tr><Th>Conf.</Th><Th>Guest</Th><Th className="text-right">Balance</Th></tr></thead>
              <tbody>{d.unpaid_balances.map((u) => (
                <tr key={u.id} className="cursor-pointer hover:bg-white/5" onClick={() => nav(`/admin/reservations/${u.id}`)}>
                  <Td className="text-gold">{u.confirmation_number}</Td><Td>{u.guest}</Td><Td className="text-right">{money(u.balance)}</Td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </Panel>
      </div>
    </div>
  );
}
